import { fetchTournament, findBroadcast, isNotFound, loadCalendar, type CalendarEntry } from "../catalog.js";
import { TOUR_IDS } from "../config.js";
import { calendarRenderSource, type RoundRenderSource } from "../live/round-state.js";
import { legacyRoundDestination, ROUND_PARAM } from "../edition-links.js";
import { handleInPlace, navigate, type Route, type View } from "../router.js";
import { closeWatchSession, openWatchSession } from "./watch-session.js";
import { banner, masthead } from '../ui/chrome.js';
import { afterNextPaint, h } from '../ui/dom.js';

/*
 * The chunk loaders live at module scope, never inside a view. Vite wraps each `import()` in its
 * preload helper, which leaves a load listener on every stylesheet `<link>` it adds — for the life
 * of the document. An `import()` written inside a view closes that listener over the view's scope,
 * pinning the first page that loaded each chunk's CSS (seen 2026-09-30 in a heap snapshot: the
 * command-palette stylesheet held a left watch page's engine panel and portraits).
 */
const loadRoundChunk = () => import("./round.js");
const loadAmbience = () => import("../ambience/layer.js");
const loadPresentation = () => import("../shared/tournament-presentation.js");
const loadArchiveRoundView = () => import('../archive/round-view.js');
const loadArchiveRound = () => import("./archive-round.js");

/*
 * Warming the watch page before the visitor opens it. Homepage card → live board is the desktop
 * visitor's one real wait: 438–613 ms on main, of which ~190 ms was downloading this page's code
 * after the click (2026-09-30, `scripts/measure-desktop-journeys.mjs`). That part can happen before it.
 * (A `<link rel=preconnect>` to the relay on hover was measured too and removed: the stream's
 * preflight paid the same 22–117 ms handshake with and without it, 5 interleaved pairs.) Idempotent.
 */
let watchChunksRequested = false;
/** Download (and evaluate) the board view's chunks: the round page and the event ambience. */
export function prefetchWatchPage(): void {
  if (watchChunksRequested) return;
  watchChunksRequested = true;
  void loadRoundChunk().catch(() => undefined);
  void loadAmbience().catch(() => undefined);
}

/**
 * An error whose message was written for the visitor. Anything else — a fetch
 * failure, a decode error, a sentence written for a log — is logged and shown as
 * {@link LOAD_FAILED}, so an internal message never reaches the page.
 */
export class VisitorError extends Error {}
const LOAD_FAILED = 'We couldn’t load this tournament. Try again in a moment.';
const visitorMessage = (error: unknown): string => {
  if (error instanceof VisitorError) return error.message;
  console.warn('[tournament] load failed', error);
  return LOAD_FAILED;
};

/**
 * `/round/:id` — a retired address that must keep working (O5, owner
 * 2026-09-22). Round pages are gone: an event has one page, `/edition/<id>`,
 * and a watched board is its board layer. Every old link — the social poster's
 * `/round/<rid>?board=<n>&utm_*`, a heat card, a bookmark — is a PERMANENT
 * redirect to `legacyRoundDestination` (`edition-links.ts`) once the round's
 * edition resolves. The Pages Function `functions/round/[id].ts` answers the
 * common case with a real 301 before any JavaScript runs; this route is the
 * client fallback (`history.replaceState`), and when NO edition resolves it
 * still renders the viewer, the only honest answer left.
 *
 * Which viewer a round id opens:
 *
 * The decision starts at `calendarRenderSource`, the web mirror of KMP
 * `CatalogProjections.roundRenderSource`, but collapses its two FINISHED arms:
 *
 * - `live` / `upcoming` → the live page, unchanged.
 * - `served` / `results-pending` → the archive page: pack + game docs, no
 *   stream (#727). Which of the two a visitor sees is decided there, on
 *   whether a pack pointer actually resolves, because the pointer plane can be
 *   ahead of the edition bake (`handlers/tournament.rs` comment D5) and
 *   `isPublished` — the server's edition-doc probe — then reads false for a
 *   round whose pack is already live. There is no `LIVE_SNAPSHOT` arm
 *   (review R5): a finished round never opens a stream either way.
 *
 * A round the calendar has never heard of resolves to the LIVE page, which is
 * this site's behaviour today and the only honest one: `Subscribe` succeeds for
 * any round id, so the stream is the last surface that can still answer.
 *
 * The exception is a link carrying the server's `?edition=` hint, which the
 * site only emits for FINISHED rounds that have left the rolling calendar. That
 * round is verified against `GetTournament` instead, and an unreachable,
 * unpublished or non-member answer is an error page — never the live page,
 * because a finished round must not open a phantom stream (#2218).
 *
 * The calendar read is `loadCalendar`, memoised for 60 s per tab, so a
 * click-through from the hub — the path a visitor actually takes during an
 * event — resolves from cache and costs the live page nothing. A calendar that
 * fails answers null, which lands on the live page too.
 */
export const roundRoutePage: View = (route, mount) => {
  const roundId = route.params["id"] ?? route.query.get("round") ?? "";
  // A legacy address resolves ITS edition and leaves (O5): the Pages Function
  // (`functions/round/[id].ts`) already 301s every link whose round the
  // calendar knows, so this arm is the client fallback — an in-app link, a
  // Function that timed out, a hinted round only `GetTournament` can place.
  return roundViewer(roundId, route.query.get("edition"), (entry) =>
    entry?.editionId ? legacyRoundDestination(roundId, entry.editionId, route.query) : null)(route, mount);
};

/**
 * `/edition/:id?round=<roundId>&board=<n>` — the board layer (O5).
 *
 * The same viewer the retired round page was (`createRoundPage` behind
 * `viewFor`: the live plane, or the archive once published), mounted on the
 * tournament page's address. The edition in the path is the `?edition=` hint
 * the old address carried, so a finished round off the rolling calendar still
 * resolves through `GetTournament`. A round the calendar files under a
 * DIFFERENT edition (a stale or hand-made link) is re-addressed there rather
 * than shown under the wrong tournament.
 */
export const roundBoardLayer: View = (route, mount) => {
  const editionId = route.params["id"] ?? "";
  const roundId = route.query.get(ROUND_PARAM) ?? "";
  const viewerRoute = { ...route, params: { id: roundId, edition: editionId } };
  return roundViewer(roundId, editionId, (entry) =>
    entry?.editionId && entry.editionId !== editionId ? legacyRoundDestination(roundId, entry.editionId, route.query) : null)(viewerRoute, mount);
};

/**
 * `/edition/:id` — the event's ONE page, the watch page (owner, 2026-09-23: "clicking on a
 * tournament from the website homepage directly leads to the watchpage"). A `?round=` names the
 * round to watch (its board layer, above); without one, the page opens on the round the shared
 * projection picks (`TournamentHomePage.watchRoundId`: the live round, else the next one — its
 * countdown, recap and standings — else the last), without writing it into the address, so
 * `/edition/<id>` keeps meaning "this event, now". The rail beside it lists every round.
 */
export const editionWatchPage: View = (route, mount) => {
  const editionId = route.params["id"] ?? "";
  const session = openWatchSession(editionId);
  // The board view's chunk, fetched while the round is being picked rather than after it — but
  // after the "Loading tournament…" frame, which it would otherwise hold back (FCP).
  void afterNextPaint().then(loadRoundChunk).catch(() => undefined);
  const controller = new AbortController();
  /** The rounds mounted, oldest first: the one on screen, then any still drawing. Each is a container and its teardown. */
  type Stage = { stage: HTMLElement; dispose: (() => void) | undefined };
  const stages: Stage[] = [];
  let current: Stage | null = null;
  let disposed = false;
  // ── Event ambience (src/ambience/layer.ts): the poster-inferred backdrop, on this page only. ──
  let stopAmbience: (() => void) | null = null;
  void loadAmbience().then(({ mountAmbience }) => { if (!disposed) stopAmbience = mountAmbience(editionId, mount); }).catch(() => undefined);

  /**
   * Mount a round's board view beside the rail. The first one shows at once; a later one (a
   * round switch in place) is built hidden and replaces the round on screen once it has drawn
   * its page — the rail moves into it in the same task, keeping its list, filters and scroll.
   */
  const showRound = (next: Route): void => {
    const previous = current;
    const stage = h('div', { class: 'watch-stage' });
    stage.style.display = previous === null ? 'contents' : 'none';
    const restoreScroll = session.rail?.holdScroll();
    const entry: Stage = { stage, dispose: undefined };
    current = entry;
    stages.push(entry);
    mount.append(stage);
    let timer = 0;
    const drawn = (): boolean => stage.childElementCount > 0 && stage.querySelector(':scope > [data-loading]') === null;
    const reveal = (): void => {
      observer.disconnect();
      clearTimeout(timer);
      if (disposed || current !== entry) return;
      stage.style.display = 'contents';
      // Every round mounted before this one goes: the one on screen, and any that never finished drawing.
      for (const older of stages.splice(0, stages.indexOf(entry))) { older.stage.remove(); older.dispose?.(); }
      restoreScroll?.();
    };
    const observer = new MutationObserver(() => { if (drawn()) reveal(); });
    if (previous !== null) {
      observer.observe(stage, { childList: true });
      // A round that never draws (a stalled read) still replaces the old one rather than hang behind it.
      timer = window.setTimeout(reveal, 15_000);
    }
    entry.dispose = roundBoardLayer(next, stage) ?? undefined;
    if (previous !== null && drawn()) reveal();
  };

  // Every later address of this route comes here first (`router.ts` `handleInPlace`): a click
  // on a board, back and forward, a standings team link. Nothing re-mounts and nothing the page
  // holds is read again; only another round opens its own board view (and stream).
  handleInPlace((next) => {
    const id = next.params["id"] ?? "";
    if (disposed || (id !== editionId && !(session.rail?.editionIds().includes(id) ?? false))) return false;
    // The edition root means “this event now”: remount to select its default
    // round or final standings instead of leaving the previous board on screen.
    if ((next.query.get(ROUND_PARAM) ?? "") === "") return false;
    session.rail?.sync(next.query);
    if (session.board?.(next.query) === true) return true;
    showRound(next);
    return true;
  });

  if ((route.query.get(ROUND_PARAM) ?? "") !== "") showRound(route);
  else {
    const waiting = h('div', { class: 'shell' }, masthead(), h('p', { class: 'note', role: 'status', text: 'Loading tournament…' }));
    mount.append(waiting);
    void (async () => {
      try {
        const { roundId, finished } = await watchRoundOf(editionId, controller.signal);
        if (controller.signal.aborted) return;
        if (roundId === null) throw new VisitorError('This tournament hasn’t started yet. Its rounds will appear here.');
        // A finished event opens on its final standings; a board picked from the rail replaces them.
        session.finalStandings = finished;
        const query = new URLSearchParams(route.query);
        query.set(ROUND_PARAM, roundId);
        waiting.remove();
        if (current === null) showRound({ ...route, query });
      } catch (error) {
        if (controller.signal.aborted) return;
        mount.replaceChildren(h('div', { class: 'shell' }, masthead(), banner(visitorMessage(error))));
      }
    })();
  }
  return () => {
    disposed = true;
    controller.abort();
    stopAmbience?.(); // event ambience
    for (const stage of stages.splice(0)) stage.dispose?.();
    current = null;
    closeWatchSession(session);
  };
};

/**
 * The round the watch page opens on, and whether the event is over.
 *
 * The calendar answers first when it can: its live round, else its next one
 * ({@link calendarWatchRound}). That read is memoised and the board view reads it next anyway,
 * so the page's first paint no longer waits on `GetTournament` — 2.3 MB on the Olympiad Open,
 * and 11 s of the Lighthouse mobile network budget before anything but "Loading tournament…"
 * could draw. Only an edition the calendar holds no live or coming round for (a finished event,
 * one aged out of the window, a calendar that failed) asks the shared projection over the
 * served schedule, which also says whether the event is over.
 */
export async function watchRoundOf(
  editionId: string,
  signal: AbortSignal,
  calendar: (tourIds: readonly string[]) => Promise<readonly CalendarEntry[]> = loadCalendar,
): Promise<{ roundId: string | null; finished: boolean }> {
  const rows = await calendar(TOUR_IDS).catch((): readonly CalendarEntry[] => []);
  const picked = calendarWatchRound(rows, editionId);
  if (picked !== null) return { roundId: picked, finished: false };
  const [{ tournament }, { tournamentHomeInput, presentationOffThread }] = await Promise.all([
    fetchTournament(editionId, signal),
    loadPresentation(),
  ]);
  const input = tournamentHomeInput([{ editionId, sectionLabel: '', tournament: tournament ?? null, pack: null }]);
  const page = JSON.parse(await presentationOffThread().project("watch", input, "", "", "", "team", 0, 1, "", "", "")) as { watchRoundId?: string | null; eventFinished?: boolean };
  return { roundId: page.watchRoundId ?? null, finished: page.eventFinished === true };
}

/**
 * The edition's live round in the calendar, else its next one; null when the calendar holds
 * neither (the caller then asks the served schedule).
 *
 * mirror-of: `TournamentHomePage.watchRoundId`'s first two arms, in
 * shared/src/commonMain/kotlin/com/chessstreamlive/viewmodel/catalog/TournamentHomePresentation.kt
 * ("the live round, else the next one"), read off the calendar: its `active` bucket is the
 * LIVE render source and its `upcoming` bucket the UPCOMING one (`calendarRenderSource`), and
 * `fetchCalendarSnapshot` already orders each bucket by start, as Kotlin orders rows by number.
 * The third arm (the last round, an event that is over) stays Kotlin's alone. Agreement test:
 * `round-route.watch-round.agreement.test.ts`.
 */
export function calendarWatchRound(rows: readonly CalendarEntry[], editionId: string): string | null {
  const own = rows.filter((row) => row.editionId === editionId);
  const pick = own.find((row) => row.state === "active") ?? own.find((row) => row.state === "upcoming");
  return pick?.entry.roundId || null;
}

/**
 * The viewer for one round id, shared by the legacy route and the board layer.
 * `redirect` answers, once the round's calendar entry is known, whether this
 * address should be replaced by another (history REPLACE, so back does not
 * bounce off it).
 */
function roundViewer(roundId: string, editionHint: string | null, redirect: (entry: CalendarEntry | null) => string | null): View {
  return (route, mount) => {
    let inner: (() => void) | undefined;
    let disposed = false;
    const controller = new AbortController();
    // The live page is its own chunk (the board, engine, stream reducer and
    // chess.js). Every arm that stays on this route renders through it — the
    // archive pages build on `createRoundPage` too — so its download starts now,
    // alongside the calendar read, rather than after it; `viewFor` then awaits the
    // same in-flight import. A failure here surfaces there. The legacy route's
    // redirect arm wastes this prefetch, which is the cheaper side of the trade:
    // the chunk is the board layer's too, so it is warm when the edition lands.
    void loadRoundChunk().catch(() => undefined);

    const show = (view: View): void => {
      if (disposed) return;
      inner?.();
      mount.replaceChildren();
      inner = view(route, mount) ?? undefined;
    };

    void (async () => {
      try {
        const entry = await findRoundEntry(roundId, editionHint, controller.signal);
        if (disposed) return;
        const destination = redirect(entry);
        if (destination) { navigate(destination, true); return; }
        show(await viewFor(entry));
      } catch (error) {
        show(archiveError(error));
      }
    })();

    return () => {
      disposed = true;
      controller.abort();
      inner?.();
    };
  };
}

/** Finished rounds outlive the rolling calendar; verify their server-supplied edition hint. */
export async function findRoundEntry(roundId: string, editionId: string | null, signal: AbortSignal): Promise<CalendarEntry | null> {
  const entry = await findBroadcast(roundId);
  if (entry || !editionId) return entry;
  let detail: Awaited<ReturnType<typeof fetchTournament>>;
  try {
    detail = await fetchTournament(editionId, signal);
  } catch (error) {
    if (signal.aborted) throw error;
    throw new VisitorError(isNotFound(error)
      ? 'We couldn’t find that tournament.'
      : 'We couldn’t reach this tournament just now. Reload to try again.');
  }
  const round = detail.tournament?.rounds.find(item => item.roundId === roundId);
  if (!round) throw new VisitorError('We couldn’t find that round in this tournament.');
  const { catalogRoundEntry } = await loadArchiveRoundView();
  return catalogRoundEntry(round, editionId, detail.tournament?.name ?? 'Tournament');
}

function archiveError(error: unknown): View {
  return (_route, mount) => { mount.append(h('div', { class: 'shell' }, masthead(), banner(visitorMessage(error)))); };
}

/**
 * The arm for a calendar entry. Async because the archive arms are code-split:
 * the pack, experience and story decoders are only downloaded by a visitor who
 * opens a finished round, so the live round page's bundle does not carry them.
 */
export async function viewFor(entry: CalendarEntry | null): Promise<View> {
  const source: RoundRenderSource = entry === null ? "live" : calendarRenderSource(entry);
  if (entry === null || source === "live" || source === "upcoming") return (await loadRoundChunk()).roundPage;
  // `served` and `results-pending` are ONE arm now, decided on real data rather
  // than on `isPublished`: the archive page resolves a pack pointer (tournament
  // first, edition second) and renders the results-pending banner itself when
  // no pointer resolves or the pack holds no games for this round.
  return (await loadArchiveRound()).archiveRoundPage(entry);
}
